import { z } from "zod";

export const longToShortEditStateSchema = z.enum([
  "QUEUED",
  "PROCESSING",
  "COMPLETE",
  "FAILED",
  "CANCELED",
]);

export type LongToShortEditState = z.infer<typeof longToShortEditStateSchema>;

export const longToShortPlatformSchema = z.enum(["ios", "web"]);

export type LongToShortPlatform = z.infer<typeof longToShortPlatformSchema>;

const legacyLongToShortPlatformSchema = z.preprocess(
  (value) => value ?? "web",
  longToShortPlatformSchema.catch("web")
);

function normalizeProjectGroupMetadata(value: unknown) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return value;
  }

  const record = value as Record<string, unknown>;
  return {
    sourceAssetId: record.sourceAssetId ?? record.source_asset_id,
    sourceUrl: record.sourceUrl ?? record.source_url,
  };
}

export const projectGroupMetadataSchema = z.preprocess(
  normalizeProjectGroupMetadata,
  z.object({
    sourceAssetId: z.string().optional().nullable(),
    sourceUrl: z.string().optional().nullable(),
  })
);

export type ProjectGroupMetadata = z.infer<typeof projectGroupMetadataSchema>;

const falseWhenNullishSchema = z.boolean().nullish().transform(Boolean);

export const longToShortSearchItemSchema = z.object({
  resultId: z.string(),
  platform: legacyLongToShortPlatformSchema,
  userId: z.string(),
  orgId: z.string().optional().nullable(),
  projectGroupId: z.string().optional().nullable(),
  projectGroupMetadata: projectGroupMetadataSchema.optional().nullable(),
  hasDownloadEvent: falseWhenNullishSchema,
  workflowId: z.string().optional().nullable(),
  editState: longToShortEditStateSchema,
  errorCode: z.string().optional().nullable(),
  error: z.string().optional().nullable(),
  createdAt: z.string().optional().nullable(),
  updatedAt: z.string().optional().nullable(),
});

export type LongToShortSearchItem = z.infer<typeof longToShortSearchItemSchema>;

export const searchLongToShortProjectsResponseSchema = z.object({
  items: z.array(longToShortSearchItemSchema),
});

export type SearchLongToShortProjectsResponse = z.infer<
  typeof searchLongToShortProjectsResponseSchema
>;

export const projectGroupStatusSchema = z.object({
  state: longToShortEditStateSchema,
  progress: z.number().optional().nullable(),
  error: z.string().optional().nullable(),
  errorCode: z.string().optional().nullable(),
});

export const projectGroupItemSourceRangeSchema = z.object({
  startMs: z.number(),
  endMs: z.number(),
});

export const projectGroupItemMetadataSchema = z.object({
  score: z.number(),
  outputAssetId: z.string(),
  sourceRanges: z.array(projectGroupItemSourceRangeSchema),
  outputAspectRatio: z.string(),
  title: z.string().optional().nullable(),
});

export const projectGroupItemSchema = z.object({
  projectId: z.string(),
  metadata: projectGroupItemMetadataSchema,
  hasDownloadEvent: falseWhenNullishSchema,
});

export type ProjectGroupItem = z.infer<typeof projectGroupItemSchema>;

export const projectGroupSchema = z.object({
  id: z.string(),
  title: z.string().optional().nullable(),
  metadata: projectGroupMetadataSchema.optional().nullable(),
  status: projectGroupStatusSchema.optional().nullable(),
  items: z.array(projectGroupItemSchema),
});

export type ProjectGroup = z.infer<typeof projectGroupSchema>;
